import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPlansRequest, markPlanCreated } from '../../api/plans/planSlice';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Stack, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import appConfig from '../../config/appConfig';

const PlanTable = () => {
  const { plans, totalCount } = useSelector((state) => state.plans);
  const [page] = useState(0);
  const dispatch = useDispatch();
  const [limit] = useState(10);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    dispatch(markPlanCreated(false));
    dispatch(fetchPlansRequest({ page: page + 1, limit: limit }));
  }, [dispatch, page, limit]);

  const handleEdit = (id) => {
    navigate(`/plans/edit/${id}`);
  };

  const handleSearchChange = useCallback(
    debounce((e) => {
      setSearchValue(e.target.value);
      dispatch(
        fetchPlansRequest({
          page: 1,
          limit: 10,
          search_key: e.target.value,
        }),
      );
    }, 500), // Adjust debounce delay as needed (500ms)
    [dispatch],
  );

  const columns = [
    {
      field: 'name',
      headerName: 'Plan',
      flex: 3.5,
    },
    {
      field: 'price',
      headerName: 'Price',
      flex: 2.7,
      valueGetter: (value, row) => {
        let price = row.price;
        let prefix = '';
        let suffix = '';

        if (row.billing_schema === 'tiered' && row.tiers && row.tiers.length > 0) {
          prefix = 'Starts at ';
          price = row.tiers[0].unit_amount;
          suffix = ' per document';
        }

        return (
          prefix +
          new Intl.NumberFormat(window.navigator.language, {
            style: 'currency',
            currency: appConfig.stripeCurrency,
          }).format(price) +
          suffix
        );
      },
    },
    {
      field: 'billing_interval',
      headerName: 'Billing Interval',
      flex: 2,
      valueGetter: (value, row) => {
        const billingInterval = row.billing_interval;
        return billingInterval ? `${billingInterval.value} ${billingInterval.unit}` : '';
      },
    },
    {
      field: 'documents_limit',
      headerName: 'Documents Limit',
      flex: 2,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.7,
      sortable: false,
      renderCell: (params) => (
        <Button variant="outlined" color="primary" size="small" onClick={() => handleEdit(params.row._id)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <>
      <Stack
        sx={{
          justifyContent: 'flex-end',
          alignItems: 'end',
          mb: 1,
        }}
      >
        <TextField
          size={'small'}
          id={'search-name'}
          label="Search by name"
          variant="outlined"
          onChange={handleSearchChange}
        />
      </Stack>
      <DataGrid
        rows={plans}
        columns={columns}
        getRowId={(row) => row._id}
        paginationMode="server"
        rowCount={totalCount}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: limit,
            },
          },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          dispatch(
            fetchPlansRequest({
              page: model.page + 1,
              limit: model.pageSize,
              search_key: searchValue,
            }),
          );
        }}
      />
    </>
  );
};

export default PlanTable;
